// SinglePeriodInventoryDiscrete.js
import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Select } from "antd";
import axios from "axios";
import UniversalModuleModal from "./UniversalModuleModal";
import { MODULE_MODAL_CONFIGS } from "./moduleModalConfigs";

const { Option } = Select;

const SinglePeriodInventoryDiscrete = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    mode: "unit",
    demand_no: 4,
    demand_levels: [100, 200, 300, 400],
    probabilities: [0.25, 0.25, 0.25, 0.25],
    revenue_per_unit: 50,
    cost_per_unit: 30,
    salvage_value_per_unit: 10,
    marginal_profit: 10,
    marginal_loss: 20
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Load existing file from DB
  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else if (initialConfig) {
      const numRows = initialConfig.numRows || initialConfig.num_rows || 4;
      setInputData({
        mode: "unit",
        demand_no: numRows,
        demand_levels: Array.from({ length: numRows }, (_, i) => (i + 1) * 100),
        probabilities: Array.from({ length: numRows }, () => Number((1 / numRows).toFixed(4))),
        revenue_per_unit: initialConfig.price !== undefined ? initialConfig.price : 50,
        cost_per_unit: initialConfig.cost !== undefined ? initialConfig.cost : 30,
        salvage_value_per_unit: initialConfig.salvage !== undefined ? initialConfig.salvage : 10,
        marginal_profit: (initialConfig.price !== undefined ? initialConfig.price : 50) - (initialConfig.cost !== undefined ? initialConfig.cost : 30),
        marginal_loss: (initialConfig.cost !== undefined ? initialConfig.cost : 30) - (initialConfig.salvage !== undefined ? initialConfig.salvage : 10)
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_single_period_discrete/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Save / Update API - Fixed to match exact API format
  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      // Create payload in EXACT format expected by API
      const payload = {
        name: fileName || "Single Period Inventory",
        input_data: {
          mode: inputData.mode,
          demand_no: inputData.demand_levels?.length || inputData.demand_no,
          demand_levels: inputData.demand_levels,
          probabilities: inputData.probabilities,
          ...(inputData.mode === "unit" && {
            revenue_per_unit: inputData.revenue_per_unit,
            cost_per_unit: inputData.cost_per_unit,
            salvage_value_per_unit: inputData.salvage_value_per_unit,
          }),
          ...(inputData.mode === "marginal" && {
            marginal_profit: inputData.marginal_profit,
            marginal_loss: inputData.marginal_loss,
          }),
        }
      };

      console.log("Sending payload:", JSON.stringify(payload, null, 2));

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_single_period_discrete/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_single_period_discrete/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      message.error(`Failed to save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update demand_no when demand_levels array changes
  const updateDemandLevels = (newDemandLevels) => {
    setInputData(prev => ({
      ...prev,
      demand_levels: newDemandLevels,
      demand_no: newDemandLevels.length
    }));
  };

  const updateProbabilities = (newProbabilities) => {
    setInputData(prev => ({
      ...prev,
      probabilities: newProbabilities
    }));
  };

  // Add/Remove rows
  const addRow = () => {
    const newDemandLevels = [...(inputData.demand_levels || []), 0];
    const newProbabilities = [...(inputData.probabilities || []), 0];
    updateDemandLevels(newDemandLevels);
    updateProbabilities(newProbabilities);
  };

  const removeRow = (index) => {
    const newDemandLevels = inputData.demand_levels.filter((_, i) => i !== index);
    const newProbabilities = inputData.probabilities.filter((_, i) => i !== index);
    updateDemandLevels(newDemandLevels);
    updateProbabilities(newProbabilities);
  };

  // ================== INPUT TABLE ==================
  const renderInputTable = () => {
    if (!inputData) return null;

    const mode = inputData.mode;

    return (
      <Card title="Input Data">
        <div style={{ marginBottom: 16 }}>
          <label>Mode: </label>
          <Select
            value={mode}
            style={{ width: 120, marginLeft: 8 }}
            onChange={(value) => setInputData(prev => ({ ...prev, mode: value }))}
          >
            <Option value="unit">Unit</Option>
            <Option value="marginal">Marginal</Option>
          </Select>
        </div>

        {mode === "unit" && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <b>Revenue per unit:</b>{" "}
              <InputNumber
                value={inputData.revenue_per_unit}
                onChange={(val) =>
                  setInputData((prev) => ({ ...prev, revenue_per_unit: val }))
                }
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Cost per unit:</b>{" "}
              <InputNumber
                value={inputData.cost_per_unit}
                onChange={(val) =>
                  setInputData((prev) => ({ ...prev, cost_per_unit: val }))
                }
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Salvage value per unit:</b>{" "}
              <InputNumber
                value={inputData.salvage_value_per_unit}
                onChange={(val) =>
                  setInputData((prev) => ({
                    ...prev,
                    salvage_value_per_unit: val,
                  }))
                }
              />
            </div>
          </div>
        )}

        {mode === "marginal" && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <b>Marginal Profit:</b>{" "}
              <InputNumber
                value={inputData.marginal_profit}
                onChange={(val) =>
                  setInputData((prev) => ({ ...prev, marginal_profit: val }))
                }
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Marginal Loss:</b>{" "}
              <InputNumber
                value={inputData.marginal_loss}
                onChange={(val) =>
                  setInputData((prev) => ({ ...prev, marginal_loss: val }))
                }
              />
            </div>
          </div>
        )}

        <Table
          bordered
          pagination={false}
          dataSource={(inputData.demand_levels || []).map((demand, i) => ({
            key: i,
            index: i,
            demand: demand,
            probability: (inputData.probabilities || [])[i] || 0,
          }))}
          columns={[
            {
              title: "Row",
              dataIndex: "index",
              render: (val) => `Row ${val + 1}`,
            },
            {
              title: "Demand",
              dataIndex: "demand",
              render: (val, record, i) => (
                <InputNumber
                  value={val}
                  onChange={(newVal) => {
                    const newDemands = [...(inputData.demand_levels || [])];
                    newDemands[i] = newVal || 0;
                    updateDemandLevels(newDemands);
                  }}
                />
              ),
            },
            {
              title: "Probability",
              dataIndex: "probability",
              render: (val, record, i) => (
                <InputNumber
                  min={0}
                  max={1}
                  step={0.01}
                  value={val}
                  onChange={(newVal) => {
                    const newProbs = [...(inputData.probabilities || [])];
                    newProbs[i] = newVal || 0;
                    updateProbabilities(newProbs);
                  }}
                />
              ),
            },
            {
              title: "Action",
              render: (_, record, i) => (
                <Button
                  type="link"
                  danger
                  onClick={() => removeRow(i)}
                  disabled={inputData.demand_levels?.length <= 1}
                >
                  Remove
                </Button>
              ),
            },
          ]}
        />

        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Button onClick={addRow} style={{ marginRight: 8 }}>
            Add Row
          </Button>
          <span>
            Probability sum: {(inputData.probabilities || []).reduce((a, b) => a + (b || 0), 0).toFixed(2)}
          </span>
        </div>

        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          style={{ marginTop: 16 }}
        >
          {isSaved ? "Update" : "Save"}
        </Button>
      </Card>
    );
  };

  // ================== OUTPUT TABLE ==================
  const renderOutputTable = () => {
    if (!results) return <p>No results yet. Save first.</p>;

    const { headers, table, summary } = results;

    // Table rendering
    const columns = headers.map((h, idx) => ({
      title: h,
      dataIndex: `col_${idx}`,
      render: (text) => {
        if (typeof text === 'number' && !isNaN(text)) {
          return text.toLocaleString();
        }
        return text;
      }
    }));

    const dataSource = table.map((row, rIdx) => {
      const obj = { key: rIdx };
      row.forEach((cell, cIdx) => {
        obj[`col_${cIdx}`] = cell;
      });
      return obj;
    });

    return (
      <Card title="Output Results">
        <Table
          bordered
          pagination={false}
          dataSource={dataSource}
          columns={columns}
          size="small"
        />

        <Card title="Summary" style={{ marginTop: 16 }}>
          <p>
            <b>Best EV:</b> {summary.best_ev.value.toLocaleString()} (Order{" "}
            {summary.best_ev.order})
          </p>
          <p>
            <b>Maximin:</b> {summary.maximin.value.toLocaleString()} (Order{" "}
            {summary.maximin.order})
          </p>
          <p>
            <b>Maximax:</b> {summary.maximax.value.toLocaleString()} (Order{" "}
            {summary.maximax.order})
          </p>
          <p>
            <b>Optimal Order Quantity:</b> {results.optimal_order_quantity}
          </p>
        </Card>
      </Card>
    );
  };

  return (
    <Card title={fileName || "Single Period Inventory"}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Input",
            children: renderInputTable()
          },
          {
            key: "output",
            label: "Output",
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default SinglePeriodInventoryDiscrete;